import React, { createContext, useCallback, useContext, useState } from 'react';

/**
 * Tenant-level branding context.
 *
 * Holds the resolved brand identity for the currently-rendered surface.
 * Populated at app boot from the most specific source available:
 *
 *   1. Brand defaults baked into the build (always available; see
 *      `apps/crm-web/src/brandConfig.ts`). Used as the `initialValue`
 *      passed to this provider.
 *   2. Tenant instance setting (`instance_setting.branding.attribution`)
 *      — overrides brand defaults post-login. Hydrated via
 *      `<TenantAttributionHydrator />` mounted inside the authenticated
 *      app tree; it fetches `/api/system/public-settings` and calls
 *      `useUpdateTenantBrandingAttribution()` to overwrite the
 *      attribution on the context.
 *   3. Storefront override (`portal_site.settings.brandAttribution`) —
 *      ecommerce only. Backlog.
 *
 * The component tree subscribes via `useTenantBranding()`. Components
 * that care about attribution (`<BrandAttributionFooter />`) read from
 * here so any upgrade to the resolution logic (e.g. plugging in the
 * storefront override) happens in one place.
 */

export interface TenantBrandingAttribution {
	show: boolean;
	label: string;
	linkUrl: string | null;
}

export interface TenantBrandingValue {
	brandAlias: string;
	brandDisplayName: string;
	attribution: TenantBrandingAttribution;
}

const TenantBrandingContext = createContext<TenantBrandingValue | null>(null);

// Separate context for the setter so consumers that only read the value
// don't re-render when the setter identity changes — and so the public
// `useTenantBranding()` API stays a single read.
type AttributionSetter = (
	attribution: Partial<TenantBrandingAttribution>,
) => void;

const TenantBrandingSetterContext = createContext<AttributionSetter | null>(
	null,
);

interface TenantBrandingProviderProps {
	/**
	 * Initial branding values. Populated from the build-time brand
	 * config (brand JSON). Post-login overrides arrive via
	 * `useUpdateTenantBrandingAttribution()`.
	 */
	initialValue: TenantBrandingValue;
	children: React.ReactNode;
}

export function TenantBrandingProvider({
	initialValue,
	children,
}: TenantBrandingProviderProps): React.ReactElement {
	const [value, setValue] = useState<TenantBrandingValue>(initialValue);

	// Stable identity so consumers (the hydrator) don't refire effects
	// on every re-render of the provider.
	const setAttribution = useCallback<AttributionSetter>((partial) => {
		setValue((prev) => ({
			...prev,
			attribution: {
				show: partial.show ?? prev.attribution.show,
				// `label` is non-null in the resolved context. When the
				// tenant override leaves the label as null/undefined we
				// keep the existing (brand-default) label so the footer
				// always has something to render.
				label: partial.label ?? prev.attribution.label,
				// `linkUrl` IS nullable in the resolved context, so a
				// tenant clearing it (null) should win. Only fall back
				// to the existing value when `partial.linkUrl` is
				// undefined (caller didn't touch it).
				linkUrl:
					partial.linkUrl === undefined
						? prev.attribution.linkUrl
						: partial.linkUrl,
			},
		}));
	}, []);

	return (
		<TenantBrandingContext.Provider value={value}>
			<TenantBrandingSetterContext.Provider value={setAttribution}>
				{children}
			</TenantBrandingSetterContext.Provider>
		</TenantBrandingContext.Provider>
	);
}

/**
 * Hook returning the resolved tenant branding. Throws if called outside
 * a `<TenantBrandingProvider>` — matches the strict-context pattern used
 * by `useTheme()` and `useTranslation()` elsewhere in this lib.
 */
export function useTenantBranding(): TenantBrandingValue {
	const context = useContext(TenantBrandingContext);

	if (context == null) {
		throw new Error(
			'useTenantBranding must be used within a TenantBrandingProvider',
		);
	}

	return context;
}

/**
 * Hook returning the setter that overwrites the tenant attribution
 * piece of the branding context. Used by `<TenantAttributionHydrator />`
 * to apply the post-login tenant override.
 *
 * Pass `null` for `linkUrl` to explicitly clear an existing URL; pass
 * `undefined` (or omit the key) to leave the current URL in place.
 */
export function useUpdateTenantBrandingAttribution(): AttributionSetter {
	const setter = useContext(TenantBrandingSetterContext);

	if (setter == null) {
		throw new Error(
			'useUpdateTenantBrandingAttribution must be used within a TenantBrandingProvider',
		);
	}

	return setter;
}
